import JSEncrypt from 'jsencrypt';
import CryptoJS from 'crypto-js';

/**
 * AsymmetricEncryptionService
 * 
 * Handles two-way RSA encryption:
 * - Pair 1: Encrypt data for backend (frontend encrypts, backend decrypts)
 * - Pair 2: Decrypt data from backend (backend encrypts, frontend decrypts)
 * 
 * Keys are loaded from window object set by backend
 */
class AsymmetricEncryptionService {
    constructor() {
        // Pair 1: Public key for encrypting data to send to backend
        // This key can only encrypt, cannot decrypt
        this.pair1PublicKey = null;
        
        // Pair 2: Private key for decrypting data received from backend
        // This key can only decrypt, cannot encrypt
        this.pair2PrivateKey = null;
        
        this.initializeKeys();
    }

    /**
     * Initialize keys from window object
     * Keys are injected by backend in master.blade.php
     */
    initializeKeys() {
        // Pair 1: Public key for frontend encryption (sent from backend API)
        if (window.__ENCRYPTION_KEYS__ && window.__ENCRYPTION_KEYS__.pair1PublicKey) {
            this.pair1PublicKey = window.__ENCRYPTION_KEYS__.pair1PublicKey;
        }

        // Pair 2: Private key for frontend decryption (injected by backend)
        if (window.__ENCRYPTION_KEYS__ && window.__ENCRYPTION_KEYS__.pair2PrivateKey) {
            this.pair2PrivateKey = window.__ENCRYPTION_KEYS__.pair2PrivateKey;
        }
    }

    /**
     * Encrypt data for backend (using Pair 1 public key)
     * Backend will decrypt using Pair 1 private key
     * 
     * @param {string|object} data Data to encrypt
     * @returns {string} Base64 encoded encrypted string
     */
    encryptForBackend(data) {
        if (!this.pair1PublicKey) {
            throw new Error('Pair 1 public key not initialized. Cannot encrypt for backend.');
        }

        // Convert object/array to JSON string if needed
        if (typeof data === 'object') {
            data = JSON.stringify(data);
        }

        const encrypt = new JSEncrypt();
        encrypt.setPublicKey(this.pair1PublicKey);
        
        // RSA has a size limit, so we need to handle large data
        // For passwords and small data, direct encryption works
        // For larger data, we'd need to chunk it, but for our use case (passwords, tokens), this is fine
        const encrypted = encrypt.encrypt(data);
        
        if (!encrypted) {
            throw new Error('Failed to encrypt data for backend. Data may be too large.');
        }

        return encrypted;
    }

    /**
     * Decrypt data from backend (using Pair 2 private key)
     * Backend encrypted using Pair 2 public key
     * Supports both direct RSA decryption and hybrid (AES+RSA) decryption
     * 
     * @param {string} encryptedData Base64 encoded encrypted string, or "encrypted_key:encrypted_data" for hybrid
     * @returns {string} Decrypted data
     */
    decryptFromBackend(encryptedData) {
        // Ensure keys are initialized
        if (!this.pair2PrivateKey) {
            this.initializeKeys();
        }
        
        if (!this.pair2PrivateKey) {
            throw new Error('Pair 2 private key not initialized. Cannot decrypt from backend.');
        }

        if (!encryptedData || encryptedData.trim() === '') {
            return '';
        }

        // Check if this is hybrid encryption (contains colon separator)
        if (encryptedData.includes(':')) {
            return this.decryptLargeDataFromBackend(encryptedData);
        }

        // Direct RSA decryption
        const decrypt = new JSEncrypt();
        decrypt.setPrivateKey(this.pair2PrivateKey);
        
        const decrypted = decrypt.decrypt(encryptedData);
        
        if (!decrypted) {
            // Get more detailed error from JSEncrypt
            const hasKey = decrypt.getKey();
            const errorMsg = hasKey 
                ? 'Decryption failed - data may be corrupted, key mismatch, or data was encrypted with different key pair'
                : 'Private key is invalid or not properly formatted';
            
            // Log additional debug info
            console.error('Decryption failed details:', {
                hasKey: hasKey,
                encryptedDataLength: encryptedData ? encryptedData.length : 0,
                encryptedDataPreview: encryptedData ? encryptedData.substring(0, 50) : 'empty',
                keyStatus: this.getKeyStatus()
            });
            
            throw new Error('Failed to decrypt data from backend: ' + errorMsg);
        }

        return decrypted;
    }

    /**
     * Decrypt large data using hybrid decryption (AES + RSA)
     * 
     * @param {string} encryptedData Format: "encrypted_key:encrypted_data" (both base64)
     * @returns {string} Decrypted data
     */
    decryptLargeDataFromBackend(encryptedData) {
        const parts = encryptedData.split(':');
        if (parts.length !== 2) {
            throw new Error('Invalid hybrid encryption format');
        }

        const [encryptedKeyBase64, encryptedDataBase64] = parts;
        
        // Decrypt AES key with RSA
        const decrypt = new JSEncrypt();
        decrypt.setPrivateKey(this.pair2PrivateKey);
        
        const decryptedKeyAndIv = decrypt.decrypt(encryptedKeyBase64);
        if (!decryptedKeyAndIv) {
            throw new Error('Failed to decrypt AES key with RSA');
        }
        

        let keyAndIvBinary;
        try {
            // Trim whitespace and try to decode as base64 (new format)
            const trimmed = decryptedKeyAndIv.trim();
            keyAndIvBinary = atob(trimmed);
            
            // Validate length - should be exactly 48 bytes (32 key + 16 IV)
            if (keyAndIvBinary.length !== 48) {
                throw new Error(`Invalid key/IV length after base64 decode: expected 48 bytes, got ${keyAndIvBinary.length}. Decrypted length: ${trimmed.length}`);
            }
        } catch (e) {
            // If base64 decode fails, this might be old format or corrupted data
            console.error('Failed to base64 decode key+IV:', e);
            throw new Error(`Failed to decode key+IV: ${e.message}. Decrypted data length: ${decryptedKeyAndIv.length}`);
        }
        
        // Extract AES key (32 bytes) and IV (16 bytes) from binary string
        const aesKeyBinary = keyAndIvBinary.substring(0, 32);
        const ivBinary = keyAndIvBinary.substring(32, 48);
        
        // Validate lengths
        if (aesKeyBinary.length !== 32 || ivBinary.length !== 16) {
            throw new Error(`Invalid key/IV extraction: key=${aesKeyBinary.length} bytes, iv=${ivBinary.length} bytes`);
        }
        
        // Convert bytes to words (big-endian, 4 bytes per word)
        const aesKeyWords = [];
        const ivWords = [];
        
        // Convert AES key bytes to words (32 bytes = 8 words)
        for (let i = 0; i < 32; i += 4) {
            const byte0 = aesKeyBinary.charCodeAt(i) & 0xFF;
            const byte1 = aesKeyBinary.charCodeAt(i + 1) & 0xFF;
            const byte2 = aesKeyBinary.charCodeAt(i + 2) & 0xFF;
            const byte3 = aesKeyBinary.charCodeAt(i + 3) & 0xFF;
            // Big-endian word: most significant byte first
            const word = (byte0 << 24) | (byte1 << 16) | (byte2 << 8) | byte3;
            aesKeyWords.push(word);
        }
        
        // Convert IV bytes to words (16 bytes = 4 words)
        for (let i = 0; i < 16; i += 4) {
            const byte0 = ivBinary.charCodeAt(i) & 0xFF;
            const byte1 = ivBinary.charCodeAt(i + 1) & 0xFF;
            const byte2 = ivBinary.charCodeAt(i + 2) & 0xFF;
            const byte3 = ivBinary.charCodeAt(i + 3) & 0xFF;
            // Big-endian word: most significant byte first
            const word = (byte0 << 24) | (byte1 << 16) | (byte2 << 8) | byte3;
            ivWords.push(word);
        }
        
        // Create WordArrays with the correct byte length
        const aesKey = CryptoJS.lib.WordArray.create(aesKeyWords, 32);
        const iv = CryptoJS.lib.WordArray.create(ivWords, 16);
        
        // Decrypt data with AES
        const decrypted = CryptoJS.AES.decrypt(
            encryptedDataBase64,
            aesKey,
            {
                iv: iv,
                mode: CryptoJS.mode.CBC,
                padding: CryptoJS.pad.Pkcs7
            }
        );
        
        // Try to convert to string, but handle errors gracefully
        let decryptedString;
        try {
            decryptedString = decrypted.toString(CryptoJS.enc.Utf8);
        } catch (error) {
            // If UTF-8 conversion fails, try to see what we got
            const hex = decrypted.toString(CryptoJS.enc.Hex);
            throw new Error(`Failed to decrypt data: Invalid UTF-8. Hex: ${hex.substring(0, 100)}...`);
        }
        
        if (!decryptedString) {
            throw new Error('Failed to decrypt data with AES - result is empty');
        }
        
        return decryptedString;
    }

    /**
     * Check if keys are initialized
     * 
     * @returns {object} Status of key initialization
     */
    getKeyStatus() {
        return {
            pair1PublicKey: !!this.pair1PublicKey,
            pair2PrivateKey: !!this.pair2PrivateKey,
            ready: !!(this.pair1PublicKey && this.pair2PrivateKey)
        };
    }
}

// Export singleton instance
export default new AsymmetricEncryptionService();

